import PouchComponent from './PouchComponent';
import TemplateComponent from "./TemplateComponent";
import { useState, useEffect, useRef } from 'react';
import { Footer, Button, Group, Title, Text, Center } from '@mantine/core';
import { getActiveBoardWithPouchById, loadTemplate, PutCell, ResetBoard, saveTemplate, UpdateCells } from '../../exports/fetch/TemplateFetch';
import MagnetSideBar from './MagnetSideBar';
import { AuthStore } from '../../stores/authStore';
import { newArchiveRecord } from '../../exports/fetch/ArchiveFetch';
import { showNotification, updateNotification } from '@mantine/notifications';
import { closeAllModals, openConfirmModal, openModal } from '@mantine/modals';
import { SquarePlus } from 'tabler-icons-react/dist';
import MagnetForm from '../AdminConsole/MagnetDesigner/MagnetForm';
import { ScreenFullIcon, ScreenNormalIcon } from '@primer/octicons-react';
import { useFullscreen } from '@mantine/hooks';
import { useDragDropManager } from 'react-dnd';
import { useNavigate } from 'react-router';
import { useSocket } from '../../contexts/SocketContext';

function Board(props) {
    const dragDropManager = useDragDropManager();
    const monitor = dragDropManager.getMonitor();
    const { toggle, fullscreen } = useFullscreen();
    const { socket, isConnected, emitRoomUpdate } = useSocket();

    const [magnets, setMagnets] = useState(props.viewData.magnetPouch);
    const [board, setBoard] = useState(props.viewData.template);
    const [magnetGroups, setMagnetGroups] = useState(props.viewData.magnetGroups);
    const [archiveDisabled, setArchiveDisabled] = useState(false);
    const [counter, setCounter] = useState(0);
    const [startCount, setStartCount] = useState(false);
    const [sideBarOpen, setSideBarOpen] = useState(false);
    const [drawerVisibility, setDrawerVisibility] = useState('visible');
    const [wordEntered, setWordEntered] = useState('');
    const [sidebarTempList, setSidebarTempList] = useState([]);
    const navigate = useNavigate();

    // Stable ref for board.id so join/leave effect doesn't re-run on board state updates
    const boardIdRef = useRef(board.id);

    useEffect(() => {
        if (!socket || !isConnected || !boardIdRef.current) return;

        socket.emit('join-room', boardIdRef.current);

        return () => {
            socket.emit('leave-room', boardIdRef.current);
        };
    }, [socket, isConnected]);

    useEffect(() => {
        const validateBoard = async () => {
            const data = await getActiveBoardWithPouchById(boardIdRef.current);
            if (data === 404) {
                navigate('/boards');
                props.setActiveTemplates([]);
                props.setActiveTab("default");
            }
        };
        validateBoard();
    }, []);

    const notifyRoom = () => {
        emitRoomUpdate(boardIdRef.current, { type: 'board-changed' });
    };

    useEffect(() => {
        if (!socket || !isConnected) return;

        const handleRoomDataChanged = async () => {
            if (!monitor.isDragging()) {
                const freshData = await getActiveBoardWithPouchById(boardIdRef.current);
                if (freshData) {
                    setMagnets(freshData.magnetPouch);
                    setBoard(freshData.template);
                    setMagnetGroups(freshData.magnetGroups);
                }
            }
        };

        const handleUserJoined = (data) => {
            if (data.username === AuthStore.username) return;
            showNotification({
                title: 'User Joined',
                message: `${data.username} joined the board`,
                color: 'blue',
                autoClose: 3000
            });
        };

        const handleUserLeft = (data) => {
            if (data.username === AuthStore.username) return;
            showNotification({
                title: 'User Left',
                message: `${data.username} left the board`,
                color: 'gray',
                autoClose: 3000
            });
        };

        socket.on('room-data-changed', handleRoomDataChanged);
        socket.on('user-joined', handleUserJoined);
        socket.on('user-left', handleUserLeft);

        return () => {
            socket.off('room-data-changed', handleRoomDataChanged);
            socket.off('user-joined', handleUserJoined);
            socket.off('user-left', handleUserLeft);
        };
    }, [socket, isConnected, monitor]);

    const sortMagnetToPouch = (magnet) => {
        setMagnets((prevList) => {
            let newList = prevList.filter(item => item.id !== magnet.id);
            return { ...newList, magnet };
        });
    };

    const moveMagnet = async (coordinates, magnet) => {
        const source = magnet.sourceLoc;
        const dest = magnet.destinationLoc;

        if (source === 'pouch' && dest === 'pouch') {
            return;
        } else if (source === 'board' && dest === 'board') {
            await removeMagnetFromTemplate(magnet);
            await addMagnetToTemplate(coordinates, magnet);
        } else if (source === 'pouch' && dest === 'board') {
            removeMagnetFromPouch(magnet);
            await addMagnetToTemplate(coordinates, magnet);
        } else if (source === 'board' && dest === 'pouch') {
            await removeMagnetFromTemplate(magnet);
            addMagnetToPouch(magnet);
        }

        setWordEntered('');
        setSidebarTempList([]);
        setDrawerVisibility('visible');
        notifyRoom();
    };

    const addMagnetToTemplate = (coordinates, magnet) => {
        return new Promise((resolve) => {
            setBoard(prevBoard => {
                if (!magnet.data.magnetgroup?.canBeCloned) {
                    const isMagnetExistOnBoard = JSON.stringify(prevBoard).includes(JSON.stringify(magnet.data));
                    if (isMagnetExistOnBoard) {
                        showNotification({
                            title: 'Error',
                            message: `Magnet "${magnet.data.magnetName}" already exists on board`,
                            color: 'red'
                        });
                        resolve();
                        return prevBoard;
                    }
                }

                const updatedBoard = {
                    ...prevBoard,
                    rows: prevBoard.rows.map((row, yIndex) => ({
                        ...row,
                        cells: row.cells.map((cell, xIndex) => {
                            if (xIndex === coordinates.x && yIndex === coordinates.y) {
                                return { ...cell, magnetId: magnet.data.id, magnet: magnet.data };
                            }
                            return cell;
                        })
                    }))
                };

                const targetCell = updatedBoard.rows[coordinates.y].cells[coordinates.x];
                PutCell(targetCell, boardIdRef.current).then(resolve);

                return updatedBoard;
            });
        });
    };

    const removeMagnetFromTemplate = (magnet) => {
        return new Promise((resolve) => {
            setBoard(prevBoard => {
                const updatedBoard = {
                    ...prevBoard,
                    rows: prevBoard.rows.map((row, yIndex) => ({
                        ...row,
                        cells: row.cells.map((cell, xIndex) => {
                            if (xIndex === magnet.coordinates.x && yIndex === magnet.coordinates.y) {
                                return { ...cell, magnetId: null, magnet: null };
                            }
                            return cell;
                        })
                    }))
                };

                const targetCell = updatedBoard.rows[magnet.coordinates.y].cells[magnet.coordinates.x];
                PutCell({ ...targetCell, magnetId: null, magnet: null }, boardIdRef.current).then(resolve);

                return updatedBoard;
            });
        });
    };

    const addMagnetToPouch = (magnet) => {
        const magnetToAdd = {
            id: magnet.data.id,
            magnetName: magnet.data.magnetName,
            magnetColor: magnet.data.magnetColor,
            textColor: magnet.data.textColor
        };
        setMagnets((prevList) => [magnetToAdd, ...prevList]);
        showNotification({
            title: 'Pouched!',
            message: `${magnet.data.magnetName} was stored in pouch.`,
            color: 'cyan',
            autoClose: 2000,
            containerWidth: 10
        });
    };

    const removeMagnetFromPouch = (magnet) => {
        if (!magnet.data.magnetgroup?.canBeCloned)
            setMagnets((prevList) => prevList.filter(item => item.id !== magnet.data.id));
    };

    const saveBoard = async () => {
        const data = await saveTemplate(board);
        if (data.ok) {
            showNotification({
                title: 'Board Default Saved',
                message: 'You have saved a new default for this board!',
                color: 'cyan'
            });
        }
    };

    const loadBoard = async () => {
        showNotification({
            id: 'defaultload',
            title: "Loading Board's Default",
            message: 'Loading Magnets...',
            loading: true,
            color: 'orange'
        });

        try {
            const data = await loadTemplate(boardIdRef.current);
            if (data) {
                setMagnets(data.magnetPouch);
                setBoard(data.template);
                UpdateCells(data.template);
                updateNotification({
                    id: 'defaultload',
                    title: 'Default Loaded',
                    message: 'The default magnet layout has been loaded',
                    loading: false,
                    color: 'cyan'
                });
                notifyRoom();
            }
        } catch (ex) {
            console.error(ex);
        }
    };

    const handleBoardReset = async () => {
        showNotification({
            id: 'clearboard',
            title: 'Clearing Board',
            message: 'Removing Magnets',
            color: 'orange',
            loading: true
        });
        await ResetBoard(boardIdRef.current);
        const boardData = await getActiveBoardWithPouchById(boardIdRef.current);
        setBoard(boardData.template);
        setMagnets(boardData.magnetPouch);
        UpdateCells(boardData.template);
        updateNotification({
            id: 'clearboard',
            title: 'Board Cleared',
            message: 'All Magnets Removed',
            color: 'cyan',
            loading: false
        });
        notifyRoom();
    };

    const archiveBoard = async () => {
        setStartCount(true);
        setCounter(10);
        setArchiveDisabled(true);

        const boardObj = {
            board,
            createdBy: AuthStore.username,
            templateId: boardIdRef.current
        };
        const data = await newArchiveRecord(boardObj);
        if (data.ok) {
            showNotification({
                title: 'Board Archived',
                message: new Date().toLocaleString(),
                color: 'cyan',
                loading: false
            });
        }
    };

    useEffect(() => {
        if (!startCount) return;
        const interval = setInterval(() => {
            setCounter((prev) => prev - 1);
        }, 1000);

        return () => {
            if (counter <= 0) {
                setStartCount(false);
                setArchiveDisabled(false);
            }
            clearInterval(interval);
        };
    }, [counter]);

    const confirmAction = (title, actionFunction) => openConfirmModal({
        title: (<Center><Title order={5}>{title}</Title></Center>),
        children: (<Text align='center'>Are you sure you wish to continue?</Text>),
        labels: { confirm: 'Yes', cancel: 'Cancel' },
        confirmProps: { variant: 'light' },
        onConfirm: () => actionFunction()
    });

    const handleMagnetCreateSubmit = async () => {
        closeAllModals();
        const boardData = await getActiveBoardWithPouchById(boardIdRef.current);
        setBoard(boardData.template);
        setMagnets(boardData.magnetPouch);
        notifyRoom();
    };

    const handleCreateMagnet = () => openModal({
        title: (<Center><Title order={5}>Create Magnet</Title></Center>),
        children: (<MagnetForm templateId={boardIdRef.current} onSubmit={handleMagnetCreateSubmit} />),
        size: 'xl',
        closeOnClickOutside: false,
    });

    const rowHeight = String(80 / board.rows.length) < 7 ? String(80 / board.rows.length) + 'vh' : '7vh';

    return (
        <>
            <div style={{ userSelect: 'none' }}>
                <div>
                    <MagnetSideBar
                        magnetGroups={magnetGroups}
                        word={{ wordEntered, setWordEntered }}
                        drawer={{ drawerVisibility, setDrawerVisibility }}
                        openState={{ sideBarOpen, setSideBarOpen }}
                        magnets={magnets}
                    />
                </div>
                <div>
                    <PouchComponent
                        openState={{ sideBarOpen, setSideBarOpen }}
                        rowHeight={rowHeight}
                        moveMagnet={moveMagnet}
                        sortMagnetToPouch={sortMagnetToPouch}
                    />
                </div>
                <div style={{ paddingTop: '15px', paddingBottom: '60px' }}>
                    <TemplateComponent
                        className="templateSlot"
                        rowHeight={rowHeight}
                        key={board.id}
                        board={board}
                        moveMagnet={moveMagnet}
                    />
                </div>
                <Footer height={60} p="md" fixed withBorder>
                    <Group position='apart'>
                        <Group position="left">
                            <Button.Group buttonBorderWidth={1}>
                                <Button mr={4} radius='md' disabled={archiveDisabled} onClick={archiveBoard}>
                                    {counter > 0 ? counter : 'Archive'}
                                </Button>
                                <Button mr={4} radius='md' onClick={() => confirmAction('Set Default Board?', saveBoard)}>Set Default</Button>
                                <Button radius='md' onClick={() => confirmAction('Load Default Board?', loadBoard)}>Load Default</Button>
                            </Button.Group>
                            <Button color='red' radius='md' variant='outline' onClick={() => confirmAction('Remove All Magnets?', handleBoardReset)}>Clear Board</Button>
                        </Group>
                        <Group position='right'>
                            <Button size="xs" color='cyan' compact onClick={handleCreateMagnet}>
                                <SquarePlus size={15} /> New Magnet
                            </Button>
                            <Button variant='subtle' compact onClick={toggle}>
                                {fullscreen ? <ScreenNormalIcon /> : <ScreenFullIcon />}
                            </Button>
                        </Group>
                    </Group>
                </Footer>
            </div>
        </>
    );
}

export default Board;